import { useState } from 'react';
import { useLocation } from 'react-router';

import {
  Stepper,
  Step,
  StepLabel,
  Typography,
  Stack,
  Box
} from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';
import { makeStyles } from 'tss-react/mui';

import Button from '@/components/button';
import Dialog from '@/components/dialog';
import {
  DEFAULT_ERRORS,
  DEFAULT_PREVIEW_RESULTS
} from '@/constants/evaluation';
import {
  useCreateEvaluation,
  useEvaluationTitleExists,
  usePreviewEvaluation
} from '@/hooks/queries/evaluations';
import useAlert from '@/hooks/useAlert';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { Evaluation } from '@/models/evaluations';
import { addDays } from '@/utils/datesAndTime';
import {
  convertFormToParameters,
  convertParametersToForm
} from '@/utils/evaluations/evaluation';
import {
  hasEmptyLogicalLevel,
  platformsEmpty
} from '@/utils/evaluations/validation';
import getRoute from '@/utils/getRoute';
import { validateString } from '@/utils/text';

import QueryDrawer from './_partials/QueryDrawer';
import Step1 from './_partials/Step1';
import Step2 from './_partials/Step2';
import Step3 from './_partials/Step3';
import Step4 from './_partials/Step4';

interface EvaluationCopy {
  title: string;
  parameters: Evaluation.Parameters;
}

type FormState = ReturnType<typeof convertFormToParameters> & { title: string };

const useStyles = makeStyles()(() => ({
  page: {
    padding: '24px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  stepper: { height: '72px' }
}));

const DEFAULT_FORM: Evaluation.Form = {
  title: '',
  choosenCreators: [],
  requestedServices: [],
  dates: [addDays(new Date(), -7), new Date()],
  followersRange: { min: '', max: '' },
  keywordQuery: {
    type: 'logicalGroup',
    operator: 'OR',
    isNegated: false,
    elements: []
  },
  highlightedKeywords: [],
  platforms: {
    youtube: { shorts: true, videos: true },
    instagram: { reels: true, posts: true, stories: true },
    tiktok: { posts: true, stories: true }
  },
  platformDisclosure: -1,
  logos: { hasLogo: 'any' },
  urls: { hasLinks: 'any', patterns: [] },
  tags: [],
  hashtags: [],
  mediaType: 'all'
};

const CreateEvaluation = () => {
  const { classes } = useStyles();
  const queryClient = useQueryClient();
  const navigate = useWorkspaceNavigate();
  const alert = useAlert();
  const location = useLocation();

  const evaluationCopy = (location.state as { evaluationCopy?: EvaluationCopy })
    ?.evaluationCopy;
  const initialDateMode = evaluationCopy?.parameters?.endCreationDate === null;

  const goToList = () => navigate(getRoute.evaluations.LIST());

  const [activeStep, setActiveStep] = useState<number>(0);
  const [isExitDialogOpen, setExitDialogOpen] = useState<boolean>(false);
  const [viewQueryOpen, setViewQueryOpen] = useState<boolean>(false);

  const [form, setForm] = useState<Evaluation.Form>(
    evaluationCopy !== undefined
      ? {
          ...convertParametersToForm(evaluationCopy.parameters),
          title: evaluationCopy.title
        }
      : DEFAULT_FORM
  );

  const [errors, setErrors] = useState<Evaluation.FormErrors>(DEFAULT_ERRORS);

  const hasVisualRecognitionSelected =
    form.requestedServices?.includes('minors');

  const { mutate: createEvaluation, isPending: isSubmitting } =
    useCreateEvaluation(
      ({ data }) => {
        queryClient.invalidateQueries({ queryKey: ['evaluations'] });
        const message = hasVisualRecognitionSelected
          ? `Evaluation "${form.title}" has been processed. Visual Recognition service requested, we will get in contact with you soon.`
          : `Evaluation "${form.title}" has been processed.`;

        alert.success(
          message,
          <Button
            variant="text"
            size="small"
            onClick={() => navigate(getRoute.evaluations.DETAIL(data.id))}
            text="open"
          />
        );
        navigate(getRoute.evaluations.DETAIL(data.id));
      },
      () => {
        alert.error(
          `Oops, something went wrong. Evaluation "${form.title}" could not be processed.`
        );
      }
    );

  const {
    data: previewResponse,
    isFetching: isLoadingPreviewResults,
    isError: errorPreview
  } = usePreviewEvaluation(convertFormToParameters(form), activeStep === 3);

  const previewResults = previewResponse ?? DEFAULT_PREVIEW_RESULTS;

  const { mutate: evaluationTitleExists } = useEvaluationTitleExists(
    ({ data }) => {
      if (Object.keys(data).length > 0) {
        setErrors((prev) => ({ ...prev, title: 'This title already exists' }));
      } else {
        setActiveStep(1);
      }
    }
  );

  const handleClickPrevious = () => {
    if (activeStep === 3) {
      queryClient.cancelQueries({ queryKey: ['previewEvaluation'] });
      queryClient.removeQueries({ queryKey: ['previewEvaluation'] });
    }
    setActiveStep((prev) => prev - 1);
  };
  const handleBackClick =
    activeStep === 0 ? () => setExitDialogOpen(true) : handleClickPrevious;

  const handleClickContinue = () => {
    switch (activeStep) {
      case 0: {
        if (!validateString(form.title, undefined, 3)) {
          setErrors((prev) => ({
            ...prev,
            title: 'Please create a title of 3 characters or more'
          }));
          return;
        }
        if (platformsEmpty(form.platforms)) {
          setErrors((prev) => ({
            ...prev,
            platforms: 'At least one platform should be selected'
          }));
          return;
        }
        if (form.title) evaluationTitleExists(form.title);
        break;
      }
      case 1:
        setActiveStep((prev) => prev + 1);
        break;
      case 2:
        setActiveStep((prev) => prev + 1);
        break;
      case 3:
        createEvaluation({
          title: form.title,
          parameters: convertFormToParameters(form)
        });
        break;
      default:
        break;
    }
  };

  const handleStep1Change = (field: keyof Evaluation.Form, value: unknown) => {
    const newForm = { ...form, [field]: value };
    const newErrors: Evaluation.FormErrors = {
      title: field === 'title' ? '' : errors.title,
      followersRange: '',
      platforms: ''
    };

    const {
      followersRange: { min, max }
    } = newForm;
    if (max !== '' && Number(min) > Number(max)) {
      newErrors.followersRange = 'Minimum value cannot be lower than maximum';
    }

    if (platformsEmpty(newForm.platforms)) {
      newErrors.platforms = 'At least one platform should be selected';
    }

    setForm(newForm);
    setErrors(newErrors);
  };

  const isContinueDisabled =
    (activeStep === 0 &&
      (!!errors.title || !!errors.followersRange || !!errors.platforms)) ||
    (activeStep === 2 && hasEmptyLogicalLevel(form.keywordQuery)) ||
    (activeStep === 3 && isLoadingPreviewResults);

  const steps = [
    {
      title: 'Name and type of content',
      content: (
        <Step1
          errors={errors}
          onChange={handleStep1Change}
          values={form}
          onClickContinue={handleClickContinue}
          initialDateMode={initialDateMode}
        />
      )
    },
    {
      title: 'Extra details',
      content: (
        <Step2
          onChange={(field, value) =>
            setForm((prev) => ({ ...prev, [field]: value }))
          }
          values={form}
          isDisabled={isSubmitting}
        />
      )
    },
    {
      title: 'Define Keywords',
      content: (
        <Step3
          onChange={(
            field: keyof FormState,
            value: FormState[keyof FormState]
          ) => setForm((prev) => ({ ...prev, [field]: value }))}
          values={form}
        />
      )
    },
    {
      title: 'Review Evaluation',
      content: (
        <Step4
          onChange={(field, value) =>
            setForm((prev) => ({ ...prev, [field]: value }))
          }
          results={previewResults}
          isLoading={isLoadingPreviewResults}
          error={errorPreview}
        />
      )
    }
  ];

  return (
    <>
      <Box className={classes.page}>
        <Typography variant="h5">Create new evaluation</Typography>

        <Stepper className={classes.stepper} activeStep={activeStep}>
          {steps.map((step) => (
            <Step key={step.title} sx={{ p: 0 }}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box flex={1} overflow="auto">
          {steps[activeStep].content}
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            {activeStep === 2 && (
              <Button
                id="evaluation-view-query"
                variant="outlined"
                text="view query"
                onClick={() => setViewQueryOpen(true)}
              />
            )}
          </Box>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="text"
              text="back"
              onClick={handleBackClick}
              disabled={isSubmitting}
            />
            <Button
              id="create-evaluation-continue"
              disabled={isContinueDisabled}
              onClick={handleClickContinue}
              text={activeStep === 3 ? 'save evaluation' : 'continue'}
              variant="contained"
              loading={isSubmitting}
            />
          </Stack>
        </Stack>
      </Box>
      <Dialog
        open={isExitDialogOpen}
        title="Leaving this page"
        dialogContent={
          <Typography>
            Are you sure you want to leave this page? Your changes will not be
            saved.
          </Typography>
        }
        actions={
          <>
            <Button
              onClick={() => setExitDialogOpen(false)}
              variant="text"
              text="cancel"
            />
            <Button id="evaluation-leave" onClick={goToList} text="leave" />
          </>
        }
      />
      <QueryDrawer
        open={viewQueryOpen}
        onClose={() => setViewQueryOpen(false)}
        query={form.keywordQuery}
      />
    </>
  );
};

export default CreateEvaluation;
